/**
 * `/study` — Prolific study mode, end to end on the participant's side.
 *
 *   link check → tech check → consent → tutorial → mic + connection check
 *     → waiting room → game 1 → game 2 → game 3 → survey → code → Prolific
 *
 * The server is the authority on where a participant is (`studyPhase`); this
 * page draws that phase and handles the steps that are the browser's alone —
 * the tech check before any socket, the interactive tutorial, the microphone
 * meter. A refresh lands back on the same step: the tech check is remembered
 * for the tab, and the server rebinds the Prolific id to the new socket.
 *
 * Nothing here navigates on unload, and nothing here is shared with the
 * friends-mode pages beyond the components it reuses.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ClientConfig,
  IceConfig,
  StudyCompletePayload,
  StudyIdlePhase,
  StudyParams,
  StudyPhasePayload,
  StudyPublicConfig,
  StudySurveyAnswers,
} from '@overcooked/shared'
import { STUDY_CONSENT_VERSION } from '@overcooked/shared'
import { EventFeed } from '../feed/feed'
import type { Connection, RoomInfo } from '../net/socket'
import { connect } from '../net/socket'
import { buildCatalog } from '../render/catalog'
import { Tutorial } from '../flow/Tutorial'
import { stepsFor } from '../flow/tutorialSteps'
import { TUTORIAL_CONFIG, TUTORIAL_MAP_NAME } from '../flow/tutorialMap'
import type { DeviceReport } from './api'
import { fetchStudyConfig, lookupFinished, reportDevice, sendTelemetry, stageHeartbeat } from './api'
import { EndScreen } from './EndScreen'
import { EntryError } from './EntryError'
import { IdleWarning } from './IdleWarning'
import { parseStudyParams, rememberTechPassed, rememberTutorialStep, techAlreadyPassed, tutorialStepFor } from './params'
import { StudyConsent } from './StudyConsent'
import type { BetweenGames } from './betweenGames'
import { betweenAfterPhase, betweenFromCountdown } from './betweenGames'
import { StudyGame } from './StudyGame'
import { StudyMicCheck } from './StudyMicCheck'
import { Survey } from './Survey'
import { submitUntilComplete } from './surveyRetry'
import { TechCheck } from './TechCheck'
import { installUnloadGuard } from './unloadGuard'
import { WaitingRoom } from './WaitingRoom'

const EMPTY_ROOM: RoomInfo = { playerId: null, roomCode: null, players: [], phase: 'lobby', sessionId: null, mapName: null }

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="page study-page">
      <header className="bar">
        <span className="brand">
          Over<em>cooked</em>
        </span>
        <span className="code">Research study</span>
      </header>
      {children}
    </main>
  )
}

export default function StudyApp() {
  // The link is read once. The format check runs now; whether a test id is
  // allowed is the server's answer, applied once the config arrives.
  const first = useMemo(() => parseStudyParams(window.location.search, true), [])
  const [config, setConfig] = useState<StudyPublicConfig | null>(null)
  const [configError, setConfigError] = useState(false)
  const verdict = useMemo(
    () => (config ? parseStudyParams(window.location.search, config.allowTestPids) : first),
    [config, first],
  )
  const params: StudyParams | null = verdict.ok ? verdict.params : null

  useEffect(() => {
    if (!first.ok) return
    void fetchStudyConfig()
      .then(setConfig)
      .catch(() => setConfigError(true))
  }, [first.ok])

  const [techPassed, setTechPassed] = useState(() => (first.ok ? techAlreadyPassed(first.params.prolificPid) : false))
  const [deviceReport, setDeviceReport] = useState<DeviceReport | null>(null)

  const feed = useMemo(() => new EventFeed(), [])
  const [connection, setConnection] = useState<Connection | null>(null)
  const connectionRef = useRef<Connection | null>(null)
  connectionRef.current = connection
  const [room, setRoom] = useState<RoomInfo>(EMPTY_ROOM)
  const [ice, setIce] = useState<IceConfig | null>(null)
  const [countdown, setCountdown] = useState<number | null>(null)
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null)

  const [phase, setPhase] = useState<StudyPhasePayload | null>(null)
  const [queue, setQueue] = useState<{ position: number; waitedMs: number; timeoutMs: number; receivedAt: number } | null>(null)
  const [game, setGame] = useState<{ roomCode: string; gameIndex: number; mapName: string } | null>(null)
  const [between, setBetween] = useState<BetweenGames | null>(null)
  const [partnerNote, setPartnerNote] = useState<{ toSurvey: boolean; requeued: boolean } | null>(null)
  const [idle, setIdle] = useState<{ phase: StudyIdlePhase; deadline: number } | null>(null)
  const [result, setResult] = useState<StudyCompletePayload | null>(null)
  const resultRef = useRef<StudyCompletePayload | null>(null)
  resultRef.current = result
  const [refused, setRefused] = useState<{ reason: string; message: string } | null>(null)
  const [survey, setSurvey] = useState<{ status: 'idle' | 'submitting' | 'error'; error: string | null }>({
    status: 'idle',
    error: null,
  })
  const [micStream, setMicStream] = useState<MediaStream | null>(null)

  // A tab that has not passed the tech check first asks whether this
  // participant has already finished: a new tab — or a device without a
  // microphone — must not stand between somebody and the code they earned.
  // A tab that passed it already goes straight to the socket, which answers
  // the same question.
  const [finishedChecked, setFinishedChecked] = useState(techPassed)
  useEffect(() => {
    if (finishedChecked || techPassed || !params || !config?.open) return
    let cancelled = false
    void lookupFinished(params).then((finished) => {
      if (cancelled) return
      if (finished) setResult((current) => current ?? finished)
      setFinishedChecked(true)
    })
    return () => {
      cancelled = true
    }
  }, [finishedChecked, techPassed, params, config?.open])

  // The game's catalogue and the latency gate's thresholds.
  useEffect(() => {
    if (!techPassed) return
    void fetch('/api/config')
      .then((response) => response.json() as Promise<ClientConfig>)
      .then(setClientConfig)
      .catch(() => undefined)
  }, [techPassed])

  // The socket opens after the tech check and outlives every phase. Each
  // (re)connect says who this is; the server puts us back where we were.
  useEffect(() => {
    if (!techPassed || !params || !config?.open) return
    const conn = connect(feed, {
      onRoom: setRoom,
      onError: (message) => console.warn(`[study] ${message}`),
      onCredentials: setIce,
      onCountdown: setCountdown,
    })
    const socket = conn.socket
    const join = (): void => {
      socket.emit('studyJoin', params)
    }
    socket.on('connect', join)
    if (socket.connected) join()
    socket.on('studyPhase', (payload) => {
      setPhase(payload)
      if (payload.phase !== 'game') {
        setGame(null)
        setBetween(null)
      } else {
        // The next game has started: the pause, and its panel, are over.
        setBetween((current) => betweenAfterPhase(current, payload.gameIndex))
      }
    })
    socket.on('studyQueue', (payload) => setQueue({ ...payload, receivedAt: Date.now() }))
    socket.on('studyGameReady', (payload) => {
      setGame(payload)
      setBetween(null)
    })
    socket.on('studyNextGameCountdown', (payload) =>
      setBetween(betweenFromCountdown(payload, Date.now(), config.betweenGamesCountdownMs)),
    )
    socket.on('studyPartnerLost', setPartnerNote)
    socket.on('studyIdleDeadline', ({ phase: idlePhase, remainingMs }) =>
      setIdle(idlePhase && remainingMs !== null ? { phase: idlePhase, deadline: Date.now() + remainingMs } : null),
    )
    socket.on('studyComplete', (payload) => {
      setResult((current) => current ?? payload)
      setIdle(null)
    })
    socket.on('studyRefused', setRefused)
    setConnection(conn)
    return () => {
      conn.disconnect()
      setConnection(null)
    }
    // `params` settles once, when the config arrives, which is before this can run.
  }, [techPassed, params, config?.open, feed])

  // A game started after a refresh has no microphone yet: the permission was
  // granted already, so asking again is silent.
  useEffect(() => {
    if (phase?.phase !== 'game' || micStream) return
    let cancelled = false
    void navigator.mediaDevices
      ?.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
      .then((stream) => {
        if (cancelled) stream.getTracks().forEach((t) => t.stop())
        else setMicStream(stream)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [phase?.phase, micStream])

  // The game between rounds: the 3-2-1 belongs to a lobby only.
  useEffect(() => {
    if (room.phase !== 'lobby') setCountdown(null)
    if (room.phase === 'playing') setBetween(null)
  }, [room.phase])

  // Activity, throttled, for the server's idle timers.
  const lastPing = useRef(0)
  const ping = useCallback(
    (force = false) => {
      const now = Date.now()
      if (!force && now - lastPing.current < (config?.activityPingMs ?? 12_000)) return
      lastPing.current = now
      connectionRef.current?.socket.emit('studyActivity')
    },
    [config?.activityPingMs],
  )
  useEffect(() => {
    if (!idle) return
    const onActivity = (): void => ping()
    const events = ['pointerdown', 'keydown', 'scroll', 'wheel'] as const
    for (const event of events) window.addEventListener(event, onActivity, { passive: true, capture: true })
    return () => {
      for (const event of events) window.removeEventListener(event, onActivity, { capture: true })
    }
  }, [idle, ping])

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!idle) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [idle])
  const idleLeft = idle ? idle.deadline - now : null
  const showIdleWarning = idleLeft !== null && config !== null && idleLeft <= config.idleWarningMs && !result
  const warned = useRef<string | null>(null)
  useEffect(() => {
    if (!showIdleWarning || !params || !idle) return
    const key = `${idle.phase}:${idle.deadline}`
    if (warned.current === key) return
    warned.current = key
    sendTelemetry(params, 'idle_warning_shown', idle.phase)
  }, [showIdleWarning, params, idle])

  // A refresh or a closed tab sends a beacon and nothing else.
  useEffect(() => {
    if (!params) return
    return installUnloadGuard(window, () => sendTelemetry(params, 'page_unload'))
  }, [params])

  // The handoff watchdog: if the code was issued but the socket never said
  // so, the heartbeat does.
  useEffect(() => {
    if (!params || !techPassed || !config?.open || result) return
    const timer = setInterval(() => {
      void stageHeartbeat(params).then((beat) => {
        if (!beat || resultRef.current || !beat.prolificRedirect || !beat.completionCode) return
        sendTelemetry(params, 'heartbeat_handoff')
        setResult({
          completionCode: beat.completionCode,
          reason: beat.outcome ?? (beat.paymentStage >= 4 ? 'completed' : 'dropped'),
          paymentStage: beat.paymentStage,
          prolificRedirect: true,
          redirectUrl: beat.redirectUrl,
        })
      })
    }, config.heartbeatMs)
    return () => clearInterval(timer)
  }, [params, techPassed, config, result])

  const tutorialSteps = useMemo(() => stepsFor(TUTORIAL_CONFIG, TUTORIAL_MAP_NAME), [])
  const tutorialCatalog = useMemo(() => buildCatalog(TUTORIAL_CONFIG), [])
  // A refresh in the tutorial resumes at the card it was on.
  const tutorialStart = useMemo(
    () => (first.ok ? tutorialStepFor(first.params.prolificPid, tutorialSteps.length) : 0),
    [first, tutorialSteps.length],
  )
  const onTutorialStep = useCallback(
    (index: number) => {
      if (first.ok) rememberTutorialStep(first.params.prolificPid, index)
    },
    [first],
  )

  const emit = connection?.socket

  // Stable for the life of the page: this component re-renders every second
  // while an idle deadline ticks, and the connection check below must not see
  // a new callback each time.
  const onMicCheckPass = useCallback(
    (stream: MediaStream, outcome: { method: 'auto' | 'manual'; medianRttMs: number }) => {
      setMicStream(stream)
      connectionRef.current?.socket.emit('studyMicCheck', {
        passed: true,
        method: outcome.method,
        medianRttMs: outcome.medianRttMs,
      })
    },
    [],
  )
  const onMicCheckFail = useCallback((failure: 'voice' | 'latency') => {
    connectionRef.current?.socket.emit('studyMicCheck', { passed: false, failure })
  }, [])

  const onTechPass = useCallback(() => {
    if (!params) return
    rememberTechPassed(params.prolificPid)
    sendTelemetry(params, 'tech_check_passed')
    setTechPassed(true)
  }, [params])
  const onIncompatible = useCallback(
    (reason: string) => {
      if (!params) return
      sendTelemetry(params, 'tech_check_failed', reason.slice(0, 120))
      // A refused device ends on the server's own completion: the code and
      // the countdown back to Prolific, the same end screen as any other.
      void reportDevice(params, (report) => {
        setDeviceReport(report)
        if (report.status === 'refused') setResult((current) => current ?? report.result)
      })
    },
    [params],
  )
  const onSlowPermission = useCallback(() => {
    if (params) sendTelemetry(params, 'mic_permission_slow')
  }, [params])

  const submitSurvey = useCallback(
    (answers: StudySurveyAnswers) => {
      const socket = connectionRef.current?.socket
      if (!socket || !config) return
      setSurvey({ status: 'submitting', error: null })
      void submitUntilComplete({
        send: () =>
          new Promise((resolve) => {
            const live = connectionRef.current?.socket
            if (!live?.connected) {
              resolve('timeout')
              return
            }
            ;(live.timeout(config.surveyAckTimeoutMs).emit as unknown as (
              event: string,
              payload: unknown,
              ack: (err: unknown, result?: { ok: boolean; error?: string }) => void,
            ) => void)('studySurveySubmit', { answers }, (err, res) => resolve(err || !res ? 'timeout' : res))
          }),
        isDone: () => resultRef.current !== null,
        maxAttempts: config.surveyMaxAttempts,
        retryDelayMs: 2_000,
        onRetry: () => params && sendTelemetry(params, 'survey_submit_retry'),
      }).then((outcome) => {
        if (outcome.outcome === 'refused') setSurvey({ status: 'error', error: outcome.error ?? "Your answers couldn't be saved." })
        else if (outcome.outcome === 'gave_up')
          setSurvey({ status: 'error', error: "We couldn't reach the server. Check your connection and press Submit again." })
      })
    },
    [config, params],
  )

  // -------------------------------------------------------------------------
  // What to draw
  // -------------------------------------------------------------------------

  if (!verdict.ok) {
    return (
      <Shell>
        <EntryError
          title={verdict.reason === 'test_pid_refused' ? 'Test links are off' : 'Study link is incomplete'}
          detail={
            verdict.reason === 'test_pid_refused'
              ? 'This server only accepts real Prolific IDs.'
              : 'Open the study again from Prolific.'
          }
        />
      </Shell>
    )
  }
  if (configError) {
    return (
      <Shell>
        <EntryError title="Can't reach the study" detail="Check your connection and reload this page." />
      </Shell>
    )
  }
  if (!config) {
    return (
      <Shell>
        <section className="gate">
          <h1>Loading…</h1>
        </section>
      </Shell>
    )
  }
  if (!config.open) {
    return (
      <Shell>
        <EntryError title="This study is closed" detail="Please return your submission on Prolific." />
      </Shell>
    )
  }

  if (result) {
    return (
      <Shell>
        <EndScreen
          result={result}
          countdownMs={config.redirectCountdownMs}
          onRedirect={(how) => params && sendTelemetry(params, how === 'auto' ? 'prolific_redirect_auto' : 'prolific_redirect_clicked')}
        />
      </Shell>
    )
  }

  if (refused?.reason === 'unavailable') {
    // Nothing was created or used up on the server; a reload simply asks again.
    return (
      <Shell>
        <section className="gate" role="alert" data-testid="study-unavailable">
          <h1>Try again soon</h1>
          <p className="hint">{refused.message}</p>
          <div className="gate-actions">
            <button type="button" onClick={() => window.location.reload()}>
              Refresh
            </button>
          </div>
        </section>
      </Shell>
    )
  }

  if (refused) {
    return (
      <Shell>
        <EntryError
          title={refused.reason === 'other_tab' ? 'Study open in another tab' : refused.reason === 'closed' ? 'This study is closed' : 'Study link is incomplete'}
          detail={refused.reason === 'closed' ? 'Please return your submission on Prolific.' : refused.message}
        />
      </Shell>
    )
  }

  if (!techPassed && !finishedChecked) {
    return (
      <Shell>
        <section className="gate">
          <h1>Loading…</h1>
        </section>
      </Shell>
    )
  }

  if (!techPassed) {
    return (
      <Shell>
        <TechCheck
          onPass={onTechPass}
          onIncompatible={onIncompatible}
          onSlowPermission={onSlowPermission}
          report={deviceReport}
        />
      </Shell>
    )
  }

  const idleModal =
    showIdleWarning && idleLeft !== null ? <IdleWarning remainingMs={idleLeft} onStillHere={() => ping(true)} /> : null

  if (!phase || !emit) {
    return (
      <Shell>
        <section className="gate">
          <h1>Connecting…</h1>
        </section>
      </Shell>
    )
  }

  switch (phase.phase) {
    case 'consent':
      return (
        <Shell>
          {idleModal}
          <StudyConsent
            retention={config.consent.retention}
            onAccept={() =>
              emit.emit('studyConsent', {
                accepted: true,
                consent: { version: STUDY_CONSENT_VERSION, variant: 'study', acceptedAt: new Date().toISOString() },
              })
            }
            onDecline={() => emit.emit('studyConsent', { accepted: false })}
          />
        </Shell>
      )
    case 'tutorial':
      return (
        <Shell>
          {idleModal}
          <Tutorial
            steps={tutorialSteps}
            catalog={tutorialCatalog}
            dishes={TUTORIAL_CONFIG.dishes}
            lead="How to play"
            interactive
            forced
            initialStep={tutorialStart}
            onStepChange={onTutorialStep}
            onDone={() => emit.emit('studyTutorialComplete')}
          />
        </Shell>
      )
    case 'mic':
      return (
        <Shell>
          {idleModal}
          <StudyMicCheck
            transport={connection?.ping ?? null}
            latency={clientConfig?.latency ?? null}
            onPass={onMicCheckPass}
            onFail={onMicCheckFail}
          />
        </Shell>
      )
    case 'waiting':
      return (
        <Shell>
          <WaitingRoom queue={queue} rescued={partnerNote?.requeued === true} />
        </Shell>
      )
    case 'game':
      if (!game || !connection || !room.playerId) {
        return (
          <Shell>
            <section className="gate">
              <h1>Partner found!</h1>
              <p className="hint">Opening your kitchen…</p>
            </section>
          </Shell>
        )
      }
      return (
        <StudyGame
          connection={connection}
          feed={feed}
          room={room}
          config={clientConfig}
          micStream={micStream}
          ice={ice}
          gameIndex={phase.gameIndex ?? game.gameIndex}
          games={phase.games}
          countdown={countdown}
          between={between}
        />
      )
    case 'survey':
      return (
        <Shell>
          {idleModal}
          {partnerNote?.toSurvey && (
            <div className="study-modal" role="alertdialog">
              <section className="gate">
                <h1>Your partner left</h1>
                <p className="hint">
                  Your partner disconnected, so the rounds are over. You'll be paid for what you
                  completed. Please finish the short survey.
                </p>
                <div className="gate-actions">
                  <button type="button" className="consent-agree" onClick={() => setPartnerNote(null)}>
                    Go to the survey
                  </button>
                </div>
              </section>
            </div>
          )}
          <Survey
            questions={config.survey}
            partnerLeft={phase.partnerDroppedMidGame}
            status={survey.status}
            error={survey.error}
            onSubmit={submitSurvey}
          />
        </Shell>
      )
    case 'done':
    default:
      return (
        <Shell>
          <section className="gate">
            <h1>Finishing…</h1>
          </section>
        </Shell>
      )
  }
}
